import {
  AccessorKeyColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Header,
  RowData,
  useReactTable,
} from '@tanstack/react-table';
import { IconArrowNarrowDown, IconArrowNarrowUp } from '@tabler/icons-react';
import { cn } from '../../lib/utils';
import { handleExport } from './export';
import {
  Table as TablePrimitive,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './components/Table';
import { Tooltip } from '../Tooltip';
import type {
  TableProps,
  TableRef,
  TableComponent,
  TableContextType,
} from './types';

import { Filter } from './components/Filter/Filter';
import { TableContext } from './hooks/useTableContext';
import { usePersistentTableState } from './hooks/usePersistentTableState';
import {
  forwardRef,
  Fragment,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ConditionalWrapper } from '../../lib/ConditionalWrapper';
import { arrIncludesArrayItem, numberInRange } from './filterFn';
import {
  getAlignmentClass,
  getFlexAlignmentClass,
  resolveTableUpdater,
} from './utils';
import useI18n from '../../hooks/useI18n';
import { Footer } from './components/Footer';

function TableInner<TData extends RowData>(
  {
    children,
    columns,
    data,
    id,
    renderExpandable = () => null,
    tableContentEndSlot,
    disableExport = false,
    exportOptions,
    className,
    containerClassName,
    pageSize = 100,
    rowsPerPageOptions = [10, 25, 50, 100],
    hideFooter = false,
    globalFilterFn = 'includesString',
    onStateChange,
    initialState,
    expanded,
    ...rest
  }: TableProps<TData>,
  ref: Ref<TableRef<TData>>,
) {
  const { t } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);

  exportOptions = { filename: 'export', ...exportOptions };

  const tableId = useMemo(() => {
    return (
      `${window.location.pathname}_${id}_` +
      columns
        .map((column) => (column as AccessorKeyColumnDef<TData>)?.accessorKey)
        .sort()
        .join('_')
    );
  }, [id, columns]);

  const state = {
    columnFilters: [],
    sorting: [
      {
        id: (columns[0]?.id ??
          (columns[0] as AccessorKeyColumnDef<TData, unknown>)
            ?.accessorKey) as string,
        desc: true,
      },
    ],
    columnVisibility: {},
    ...initialState,
  };

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize,
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [tableState, setTableState] = usePersistentTableState(tableId, state, {
    restore: (storedState, initialState) => {
      const filters = [
        ...(storedState.columnFilters ?? []),
        ...initialState.columnFilters,
      ];
      const columnFilters = Array.from(
        new Map(filters.map((item) => [item.id, item])).values(),
      );

      return {
        sorting: storedState.sorting ?? initialState.sorting,
        columnFilters,
        columnVisibility:
          storedState.columnVisibility ?? initialState.columnVisibility,
      };
    },
  });

  useEffect(() => {
    onStateChange?.(tableState);
  }, [onStateChange, tableState]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),

    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: (updater) => {
      if (containerRef?.current) {
        containerRef.current.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
      setPagination(updater);
    },

    getSortedRowModel: getSortedRowModel(),
    onSortingChange: (updater) =>
      setTableState((prev) => ({
        ...prev,
        sorting: resolveTableUpdater(updater, prev.sorting ?? []),
      })),
    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: globalFilterFn || 'includesString',
    onColumnFiltersChange: (updater) =>
      setTableState((prev) => ({
        ...prev,
        columnFilters: resolveTableUpdater(updater, prev.columnFilters ?? []),
      })),
    onColumnVisibilityChange: (updater) =>
      setTableState((prev) => ({
        ...prev,
        columnVisibility: resolveTableUpdater(
          updater,
          prev.columnVisibility ?? {},
        ),
      })),

    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: setExpandedRows,

    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),

    state: {
      ...tableState,
      pagination,
      globalFilter,
      expanded: expandedRows,
    },

    filterFns: {
      arrIncludesArrayItem,
      numberInRange,
    },
    ...rest,
  });

  useEffect(() => {
    if (expanded) {
      setExpandedRows(expanded);
    }
  }, [expanded]);

  const exportTableData = useCallback(() => {
    if (disableExport) return;
    handleExport(table.getPrePaginationRowModel().rows, columns, exportOptions);
  }, [columns, disableExport, exportOptions, table]);

  useImperativeHandle(
    ref,
    (): TableRef<TData> => ({
      table,
      tableId,
      exportTableData,
    }),
    [table, tableId, exportTableData],
  );

  const contextValue: TableContextType<TData> = {
    isFullscreen,
    setIsFullscreen,
    globalFilter,
    setGlobalFilter,
    tableState,
    table,
  };

  function sort(header: Header<TData, unknown>) {
    header.column.toggleSorting(
      header.column.getIsSorted()
        ? header.column.getIsSorted() === 'asc'
        : true,
    );
  }

  const tableContent = (
    <div
      className={cn(
        'eye:flex eye:flex-col',
        isFullscreen
          ? 'eye:grow-y eye:fixed eye:inset-[0] eye:z-10000 eye:bg-white eye:p-xs'
          : 'eye:w-full',
      )}
    >
      {children}
      <div
        className={cn(
          'eye:rounded-md eye:border eye:border-grey-40',
          'eye:w-full eye:flex-1 eye:overflow-x-auto',
        )}
      >
        <div
          ref={containerRef}
          className={cn(
            !isFullscreen && 'eye:max-h-[70dvh]',
            'eye:w-full eye:overflow-y-auto',
            containerClassName,
          )}
        >
          <TablePrimitive className={cn('eye:w-full', className)}>
            <TableHeader className={'eye:sticky eye:top-[0] eye:z-10'}>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      style={{
                        width: header.column.columnDef.size ?? header.getSize(),
                        minWidth:
                          header.column.columnDef.minSize ?? header.getSize(),
                        maxWidth:
                          header.column.columnDef.maxSize ?? header.getSize(),
                      }}
                      key={header.id}
                    >
                      <span
                        className={cn(
                          'eye:flex eye:w-full eye:items-center eye:overflow-hidden',
                          getFlexAlignmentClass(
                            header.column.columnDef.meta?.align,
                          ),
                        )}
                      >
                        <ConditionalWrapper
                          condition={header.column.getCanSort()}
                          wrapper={(children) => (
                            <Tooltip
                              content={`${header.column.columnDef.header}
                              ${
                                header.column.getIsSorted()
                                  ? header.column.getIsSorted() === 'asc'
                                    ? `(${t('sortedAsc')})`
                                    : `(${t('sortedDesc')})`
                                  : ''
                              }
                              `}
                            >
                              <span
                                tabIndex={0}
                                className='eye:flex eye:min-w-0 eye:cursor-pointer eye:items-center eye:truncate'
                                role='button'
                                aria-label={
                                  t('sortBy') +
                                  ': ' +
                                  header.column.columnDef.header
                                }
                                onClick={() => sort(header)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    // prevents page scroll on Space
                                    e.preventDefault();
                                    sort(header);
                                  }
                                }}
                              >
                                <span className='eye:truncate'>{children}</span>
                                {header.column.getIsSorted() ? (
                                  header.column.getIsSorted() === 'asc' ? (
                                    <IconArrowNarrowUp
                                      className='eye:shrink-0'
                                      aria-label={t('sortAscending') as string}
                                    />
                                  ) : (
                                    <IconArrowNarrowDown
                                      className='eye:shrink-0'
                                      aria-label={t('sortDescending') as string}
                                    />
                                  )
                                ) : null}
                              </span>
                            </Tooltip>
                          )}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </ConditionalWrapper>
                      </span>
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getPaginationRowModel().rows.length ? (
                table.getPaginationRowModel().rows.map((row) => (
                  <Fragment key={row.id}>
                    <TableRow
                      className='eye:bg-white'
                      data-state={row.getIsSelected() && 'selected'}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          className={cn(
                            getAlignmentClass(
                              cell.column.columnDef.meta?.align,
                            ),
                          )}
                          style={{
                            width:
                              cell.column.columnDef.size ??
                              cell.column.getSize(),
                            minWidth:
                              cell.column.columnDef.minSize ??
                              cell.column.getSize(),
                            maxWidth:
                              cell.column.columnDef.maxSize ??
                              cell.column.getSize(),
                          }}
                          key={cell.id}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                    {renderExpandable(row)}
                  </Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className='eye:p-32 eye:text-center'
                  >
                    {t('noResultsFound')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </TablePrimitive>
        </div>
        {tableContentEndSlot}
      </div>
      {!hideFooter && (
        <Footer
          table={table}
          exportTableData={exportTableData}
          isFullscreen={isFullscreen}
          disableExport={disableExport}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      )}
    </div>
  );

  return (
    <TableContext.Provider value={contextValue as TableContextType<unknown>}>
      {isFullscreen ? createPortal(tableContent, document.body) : tableContent}
    </TableContext.Provider>
  );
}

const TableBase = forwardRef(TableInner) as unknown as TableComponent;

TableBase.Filter = Filter;

export const Table = TableBase;
